'use client';

import { Dialog, Transition } from '@headlessui/react';
import { cn } from '@ui/lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  createContext,
  Fragment,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { BadgeWithBorder } from '@ui/Badges';
import { SmileIcon } from '@ui/Icons';
import { CheckForUpdatesDialog } from '../check-for-updates-dialog';

import { ChevronDownIcon, ChevronLeftIcon } from '@heroicons/react/20/solid';
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Cog6ToothIcon,
  KeyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BlurredDivider } from '@ui/BlurredDivider';
import { ClickToCopyInput } from '@ui/core/click-to-copy-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/core/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/core/tooltip';

export type NavigationItem = {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  current?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

export type NavigationGroup = NavigationItem[];

export type UserInfo = {
  name?: string;
  role?: string;
  photoUrl?: string;
};

export type BusinessInfo = {
  name?: string;
  profilePhotoUrl?: string;
};

type NavigationProps = {
  isMobile: boolean;
  isCollapsed?: boolean;
  setIsCollapsed?: (value: boolean) => void;
  navigation: NavigationGroup[];
  userInfo?: UserInfo;
  businessInfo?: BusinessInfo;
  businessAccountId?: string;
  logoSrc: string;
  logoCollapsedSrc?: string;
  primaryColor?: string;
  logoutHandler: () => void;
  closeSidebar?: () => void;
  isAuthenticated?: boolean;
};

const getStaggeredDelay = (index: number, isClosing: boolean = false) => {
  const baseDelay = isClosing ? 100 : 200;
  const increment = isClosing ? 50 : 100;
  return `delay-[${baseDelay + index * increment}ms]`;
};

const isInternalLink = (url: string): boolean => {
  return url.startsWith('/') && !url.startsWith('//');
};

// Format role names by adding spaces between camelCase words
const formatRoleName = (role: string): string => {
  if (!role) return role;
  // Add space before capital letters that are not at the beginning
  return role.replace(/([A-Z])/g, ' $1').trim();
};

// Fallback profile photo component with SmileIcon
const FallbackProfilePhoto = ({
  className,
  size = 'default',
}: {
  className?: string;
  size?: 'small' | 'default' | 'large';
}) => {
  const sizeClasses = {
    small: 'h-6 w-6',
    default: 'h-10 w-10',
    large: 'h-12 w-12',
  };

  const iconSizeClasses = {
    small: 'h-3 w-3',
    default: 'h-5 w-5',
    large: 'h-6 w-6',
  };

  return (
    <div
      className={cn(
        'rounded-full bg-white flex items-center justify-center border border-slate-200',
        sizeClasses[size],
        className
      )}
    >
      <SmileIcon className={cn('text-slate-800', iconSizeClasses[size])} />
    </div>
  );
};

const Navigation = ({
  isMobile,
  isCollapsed,
  setIsCollapsed,
  navigation,
  userInfo,
  businessInfo,
  businessAccountId,
  logoSrc,
  logoCollapsedSrc,
  primaryColor = '#a21caf', // Default to fuchsia
  logoutHandler,
  closeSidebar,
  isAuthenticated = true,
}: NavigationProps) => {
  const currentPath = usePathname() || '';
  // Check if this is the pet owner app (based on primary color being petboost red)
  const isPetOwnerApp = primaryColor === '#ff5349';

  const navMenu = (
    <div
      className={cn(
        'flex grow flex-col px-4 pr-4 sm:bg-slate-50 lg:bg-transparent sm:shadow-[4px_0_6px_-1px_rgba(148,163,184,0.15)] lg:shadow-none',
        isMobile && [
          'motion-safe:animate-in fade-in slide-in-from-left duration-700',
          'motion-safe:animate-out fade-out slide-out-to-left duration-500',
        ]
      )}
    >
      {/* Logo section */}
      <div
        className={cn(
          'flex h-16 shrink-0 items-center',
          isCollapsed ? 'justify-center' : 'px-2.5',
          isMobile && [
            'motion-safe:animate-in fade-in-40 slide-in-from-left-4 duration-700',
            'motion-safe:animate-out fade-out slide-out-to-left duration-500',
            getStaggeredDelay(0),
          ]
        )}
      >
        <Image
          src={isCollapsed && logoCollapsedSrc ? logoCollapsedSrc : logoSrc}
          alt="Logo"
          width={0}
          height={0}
          sizes="100vw"
          style={{
            width: isCollapsed ? '40px' : '124px',
            height: 'auto',
            maxHeight: isCollapsed ? '24px' : 'none',
          }}
          className={cn(
            'transition-all duration-300 object-contain',
            isCollapsed ? 'initial-size-small rounded-sm' : ''
          )}
        />
      </div>

      {/* Business Card or User Profile (if businessInfo is null) */}
      <div
        className={cn(
          isPetOwnerApp && !isCollapsed ? 'mb-2' : 'mb-2',
          isMobile && [
            'motion-safe:animate-in fade-in-40 slide-in-from-left-4 duration-700',
            'motion-safe:animate-out fade-out slide-out-to-left duration-500',
            getStaggeredDelay(1),
          ]
        )}
      >
        {isPetOwnerApp && !isCollapsed ? (
          <div className="mb-0 w-full h-auto flex-1">
            {businessInfo ? (
              <BusinessCard
                isCollapsed={isCollapsed}
                businessInfo={businessInfo}
                businessAccountId={businessAccountId}
                primaryColor={primaryColor}
              />
            ) : userInfo ? (
              <ProfileDropDown
                isCollapsed={isCollapsed}
                isMobileView={false}
                userInfo={userInfo}
                logoutHandler={logoutHandler}
                isAtBottom={false}
                primaryColor={primaryColor}
                isAuthenticated={isAuthenticated}
              />
            ) : null}
          </div>
        ) : (
          <>
            {businessInfo ? (
              <BusinessCard
                isCollapsed={isCollapsed}
                businessInfo={businessInfo}
                businessAccountId={businessAccountId}
                primaryColor={primaryColor}
              />
            ) : userInfo ? (
              <ProfileDropDown
                isCollapsed={isCollapsed}
                isMobileView={false}
                userInfo={userInfo}
                logoutHandler={logoutHandler}
                isAtBottom={false}
                primaryColor={primaryColor}
                isAuthenticated={isAuthenticated}
              />
            ) : null}
          </>
        )}
      </div>

      {/* Navigation items */}
      <nav className="flex flex-1 flex-col">
        <ul role="list">
          {navigation.map((group, groupIndex) => (
            <li
              key={groupIndex}
              className={cn(
                isMobile && [
                  'motion-safe:animate-in fade-in-40 slide-in-from-left-4 duration-700',
                  'motion-safe:animate-out fade-out slide-out-to-left duration-500',
                  getStaggeredDelay(groupIndex + 2),
                ]
              )}
            >
              <ul role="list" className="space-y-0.5">
                {group.map((item, itemIndex) => (
                  <li
                    key={item.name}
                    className={cn(
                      isMobile && [
                        'motion-safe:animate-in fade-in-40 slide-in-from-left-4 duration-700',
                        'motion-safe:animate-out fade-out slide-out-to-left duration-500',
                        getStaggeredDelay(groupIndex * 4 + itemIndex + 3),
                      ]
                    )}
                  >
                    {item.disabled ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              'flex items-center relative cursor-not-allowed',
                              isCollapsed ? 'justify-center' : 'px-2',
                              'transition-all duration-200 text-sm border border-transparent rounded-lg',
                              'text-slate-600 p-2 opacity-60'
                            )}
                          >
                            <item.icon
                              className={cn(
                                isCollapsed
                                  ? 'h-[18px] w-[18px]'
                                  : 'h-[18px] w-[18px]',
                                'transition-[width,height] duration-300 ease-in-out text-slate-600'
                              )}
                            />
                            {!isCollapsed && (
                              <span className="ml-3 text-ellipsis">
                                {item.name}
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{item.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : isInternalLink(item.href) ? (
                      <Link
                        href={item.href}
                        className={cn(
                          'flex items-center cursor-pointer relative',
                          isCollapsed ? 'justify-center' : 'px-2',
                          'transition-all duration-200 text-sm border border-transparent rounded-lg',
                          currentPath.startsWith(item.href)
                            ? cn(
                                'bg-white shadow-xs shadow-slate-950/5 font-bold ring-1 ring-slate-200/65',
                                isCollapsed ? 'p-[7px] pl-[3px]' : 'p-[7px]'
                              )
                            : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-xs hover:shadow-slate-950/5 hover:border-slate-200/50 p-2'
                        )}
                        onClick={
                          isMobile && closeSidebar ? closeSidebar : undefined
                        }
                      >
                        {currentPath.startsWith(item.href) && (
                          <div
                            className="absolute top-[4.5px] bottom-[4.5px] right-1 w-[4.5px] rounded-sm"
                            style={{ backgroundColor: primaryColor }}
                          />
                        )}
                        <item.icon
                          className={cn(
                            isCollapsed
                              ? 'h-[18px] w-[18px]'
                              : 'h-[18px] w-[18px]',
                            'transition-[width,height] duration-300 ease-in-out'
                          )}
                        />
                        {!isCollapsed && (
                          <span className="ml-3 text-ellipsis">
                            {item.name}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        className={cn(
                          'flex items-center cursor-pointer relative',
                          isCollapsed ? 'justify-center' : 'px-2',
                          'transition-all duration-200 text-sm border border-transparent rounded-lg',
                          currentPath.startsWith(item.href)
                            ? cn(
                                'bg-white shadow-xs shadow-slate-950/5 font-bold ring-1 ring-slate-200/65',
                                isCollapsed ? 'p-[7px] pl-[3px]' : 'p-[7px]'
                              )
                            : 'text-slate-600 hover:text-slate-800 hover:bg-white hover:shadow-xs hover:shadow-slate-950/5 hover:border-slate-200/50 p-2'
                        )}
                        onClick={
                          isMobile && closeSidebar ? closeSidebar : undefined
                        }
                      >
                        {currentPath.startsWith(item.href) && (
                          <div
                            className="absolute top-[4.5px] bottom-[4.5px] right-1 w-[4.5px] rounded-sm"
                            style={{ backgroundColor: primaryColor }}
                          />
                        )}
                        <item.icon
                          className={cn(
                            isCollapsed
                              ? 'h-[18px] w-[18px]'
                              : 'h-[18px] w-[18px]',
                            'transition-[width,height] duration-300 ease-in-out'
                          )}
                        />
                        {!isCollapsed && (
                          <span className="ml-3 text-ellipsis">
                            {item.name}
                          </span>
                        )}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              {groupIndex < navigation.length - 1 && (
                <div
                  className={cn(
                    'my-4 border-t border-slate-200',
                    isMobile && [
                      'motion-safe:animate-in fade-in-40 duration-500',
                      'motion-safe:animate-out fade-out duration-300',
                      getStaggeredDelay(groupIndex * 4 + 4),
                    ]
                  )}
                />
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Profile section - only shown at bottom if businessInfo exists */}
      {businessInfo && userInfo && (
        <div
          className={cn(
            '',
            isMobile && [
              'motion-safe:animate-in fade-in-40 slide-in-from-left-4 duration-500',
              'motion-safe:animate-out fade-out slide-out-to-left duration-300',
              getStaggeredDelay(navigation.length * 4 + 5),
            ]
          )}
        >
          <div
            className={cn(
              'flex items-center',
              isCollapsed ? 'justify-center p-2' : 'py-3'
            )}
          >
            {isPetOwnerApp && !isCollapsed ? (
              <div className="w-full">
                <ProfileDropDown
                  isCollapsed={isCollapsed}
                  isMobileView={false}
                  userInfo={userInfo}
                  logoutHandler={logoutHandler}
                  isAtBottom={true}
                  primaryColor={primaryColor}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            ) : (
              <ProfileDropDown
                isCollapsed={isCollapsed}
                isMobileView={false}
                userInfo={userInfo}
                logoutHandler={logoutHandler}
                isAtBottom={true}
                primaryColor={primaryColor}
                isAuthenticated={isAuthenticated}
              />
            )}
          </div>
        </div>
      )}

      {/* Collapse button */}
      {!isMobile && setIsCollapsed && (
        <div
          className={cn(
            'absolute -right-3.5 top-1/4 transform -translate-y-1/2',
            'animate-in fade-in slide-in-from-left duration-300',
            getStaggeredDelay(navigation.length * 4 + 6)
          )}
        >
          <button
            onClick={() => {
              const newState = !isCollapsed;
              setIsCollapsed(newState);
              // Dispatch custom event for sidebar state change
              window.dispatchEvent(
                new CustomEvent('sidebarStateChange', {
                  detail: { isCollapsed: newState },
                })
              );
            }}
            className="inline-flex items-center justify-center rounded-lg bg-white p-1.5 text-slate-800 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
          >
            <ChevronLeftIcon
              className={cn(
                'h-4 w-4 text-slate-700 transition-transform duration-300',
                isCollapsed && 'rotate-180'
              )}
            />
          </button>
        </div>
      )}
    </div>
  );

  return isMobile ? (
    navMenu
  ) : (
    <div
      className={cn(
        'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col',
        'transition-all duration-300 ease-in-out',
        isCollapsed ? 'lg:w-20' : 'lg:w-72'
      )}
    >
      {navMenu}
    </div>
  );
};

const BusinessCard = ({
  isCollapsed,
  businessInfo,
  primaryColor,
  businessAccountId,
}: {
  isCollapsed?: boolean;
  businessInfo: BusinessInfo;
  primaryColor?: string;
  businessAccountId?: string;
}) => {
  if (!businessInfo) return null;

  // Check if this is the pet owner app (based on primary color being petboost red)
  const isPetOwnerApp = primaryColor === '#ff5349';

  // Generate the booking link if businessAccountId is available
  const bookingLink = businessAccountId
    ? `http://app.petboost.com/booking-form?businessAccountId=${businessAccountId}`
    : '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full bg-white rounded-lg shadow-[4px_4px_8px_-1px_rgba(0,0,0,0.05)] py-2 px-3 cursor-pointer text-left min-h-fit h-auto outline-none focus:outline-none ">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-x-3">
            <div className="flex-shrink-0">
              {businessInfo.profilePhotoUrl ? (
                <Image
                  src={businessInfo.profilePhotoUrl}
                  alt=""
                  width={10}
                  height={10}
                  className={cn(
                    'rounded-full object-cover',
                    isCollapsed ? 'h-6 w-6' : 'h-10 w-10'
                  )}
                />
              ) : (
                <FallbackProfilePhoto
                  size={isCollapsed ? 'small' : 'default'}
                />
              )}
            </div>
            {!isCollapsed && (
              <div className="flex flex-col justify-center">
                <div className="text-xs font-bold leading-5 text-slate-800">
                  {businessInfo.name}
                </div>
                {!isPetOwnerApp && (
                  <div className="mt-0.5">
                    <BadgeWithBorder color="fuchsia">
                      Business Partner
                    </BadgeWithBorder>
                  </div>
                )}
              </div>
            )}
          </div>
          <ChevronDownIcon
            className="h-4 w-4 text-slate-600"
            aria-hidden="true"
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isCollapsed ? 'center' : 'start'}
        className="w-[280px]"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="opacity-60 cursor-not-allowed">
              <DropdownMenuItem disabled className="cursor-not-allowed">
                <Cog6ToothIcon className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              Self-service for this launching very soon. for help, contact
              Frazer 👋
            </p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuItem asChild>
          {isInternalLink('/your-listing') ? (
            <Link href="/your-listing" className="flex w-full items-center">
              {businessInfo.profilePhotoUrl ? (
                <Image
                  src={businessInfo.profilePhotoUrl}
                  alt=""
                  width={16}
                  height={16}
                  className="mr-2 h-4 w-4 rounded-full"
                />
              ) : (
                <div className="mr-2">
                  <FallbackProfilePhoto size="small" className="h-4 w-4" />
                </div>
              )}
              Your Listing Page
            </Link>
          ) : (
            <a href="/your-listing" className="flex w-full items-center">
              {businessInfo.profilePhotoUrl ? (
                <Image
                  src={businessInfo.profilePhotoUrl}
                  alt=""
                  width={16}
                  height={16}
                  className="mr-2 h-4 w-4 rounded-full"
                />
              ) : (
                <div className="mr-2">
                  <FallbackProfilePhoto size="small" className="h-4 w-4" />
                </div>
              )}
              Your Listing Page
            </a>
          )}
        </DropdownMenuItem>

        <BlurredDivider className="my-2" />

        <div className="p-3">
          <ClickToCopyInput
            value={bookingLink}
            label="Quick-copy Booking Link:"
            size="sm"
            className="w-full"
            placeholder="Booking link will appear here"
            emptyStateButton={
              <div className="text-xs text-slate-500 p-2 text-center">
                Business account ID not available
              </div>
            }
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ProfileDropDown = ({
  isCollapsed,
  isMobileView,
  hideNameAndBadge,
  userInfo,
  logoutHandler,
  isAtBottom = false,
  primaryColor,
  isAuthenticated = true,
}: {
  isCollapsed?: boolean;
  isMobileView?: boolean;
  hideNameAndBadge?: boolean;
  userInfo: UserInfo;
  logoutHandler: () => void;
  isAtBottom?: boolean;
  primaryColor?: string;
  isAuthenticated?: boolean;
}) => {
  // Check if this is the pet owner app (based on primary color being petboost red)
  const isPetOwnerApp = primaryColor === '#ff5349';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="w-full bg-white rounded-lg shadow-[4px_4px_8px_-1px_rgba(0,0,0,0.05)] py-2 px-3 cursor-pointer text-left min-h-fit h-auto outline-none focus:outline-none ">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center gap-x-3">
            <div className="flex-shrink-0">
              {userInfo?.photoUrl ? (
                <Image
                  src={userInfo.photoUrl}
                  alt=""
                  width={10}
                  height={10}
                  className={cn(
                    'rounded-full object-cover',
                    isMobileView
                      ? 'h-8 w-8'
                      : isCollapsed
                        ? 'h-6 w-6'
                        : 'h-10 w-10'
                  )}
                />
              ) : (
                <FallbackProfilePhoto
                  size={
                    isMobileView ? 'default' : isCollapsed ? 'small' : 'default'
                  }
                  className={
                    isMobileView
                      ? 'h-8 w-8'
                      : isCollapsed
                        ? 'h-6 w-6'
                        : 'h-10 w-10'
                  }
                />
              )}
            </div>
            {!isCollapsed && !hideNameAndBadge && (
              <div className="flex flex-col justify-center">
                <div className="text-xs font-bold leading-5 text-slate-800">
                  {userInfo?.name}
                </div>
                <div className="mt-0.5">
                  <BadgeWithBorder color={isPetOwnerApp ? 'pbred' : 'slate'}>
                    {formatRoleName(userInfo?.role || '')}
                  </BadgeWithBorder>
                </div>
              </div>
            )}
          </div>
          <ChevronDownIcon
            className="h-4 w-4 text-slate-800"
            aria-hidden="true"
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isCollapsed ? 'center' : 'start'}
        className="w-[200px]"
        side={isAtBottom ? 'top' : 'bottom'}
      >
        {!isAuthenticated && isPetOwnerApp && (
          <DropdownMenuItem asChild>
            <a href="/reset-password" className="flex w-full items-center">
              <KeyIcon className="mr-2 h-4 w-4" />
              Change Password
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <CheckForUpdatesDialog />
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logoutHandler}>
          <div className="flex w-full items-center">
            {isAuthenticated ? (
              <ArrowRightOnRectangleIcon className="mr-2 h-4 w-4" />
            ) : (
              <ArrowLeftOnRectangleIcon className="mr-2 h-4 w-4" />
            )}
            {isAuthenticated ? 'Sign out' : 'Sign in'}
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Context for dynamic background
type SetBackgroundClassNameFn = (className: string) => void;
export const LayoutBackgroundContext = createContext<SetBackgroundClassNameFn>(
  () => {}
);

// Context for dynamic logo
type SetLogoSrcFn = (logoSrc: string, logoCollapsedSrc?: string) => void;
export const LayoutLogoContext = createContext<SetLogoSrcFn>(() => {});

// Context for dynamic primary color
type SetPrimaryColorFn = (primaryColor: string) => void;
export const LayoutPrimaryColorContext = createContext<SetPrimaryColorFn>(
  () => {}
);

/**
 * Hook to dynamically set layout background class via context.
 * @param className Tailwind or CSS class for background styling.
 */
export function useLayoutBackground(className: string) {
  const setBgClass = useContext(LayoutBackgroundContext);
  const currentClassRef = useRef<string | null>(null);

  useEffect(() => {
    // Only update if the className has actually changed
    if (currentClassRef.current !== className) {
      setBgClass(className);
      currentClassRef.current = className;
    }

    // Reset to default on unmount
    return () => {
      if (currentClassRef.current === className) {
        setBgClass('bg-white');
        currentClassRef.current = null;
      }
    };
  }, [className, setBgClass]);
}

/**
 * Hook to dynamically set layout logo via context.
 * @param logoSrc URL for the main logo
 * @param logoCollapsedSrc Optional URL for the collapsed logo
 */
export function useLayoutLogo(logoSrc: string, logoCollapsedSrc?: string) {
  const setLogo = useContext(LayoutLogoContext);
  const currentLogoRef = useRef<{
    main: string | null;
    collapsed?: string | null;
  }>({ main: null, collapsed: null });

  useEffect(() => {
    // Only update if the logo sources have actually changed
    if (
      currentLogoRef.current.main !== logoSrc ||
      currentLogoRef.current.collapsed !== logoCollapsedSrc
    ) {
      setLogo(logoSrc, logoCollapsedSrc);
      currentLogoRef.current = { main: logoSrc, collapsed: logoCollapsedSrc };
    }

    // Reset to default on unmount - this should be handled by the component that sets initial values
    // We don't reset here because other components might want to maintain the logo
  }, [logoSrc, logoCollapsedSrc, setLogo]);
}

/**
 * Hook to dynamically set layout primary color via context.
 * @param primaryColor Hex color string for primary theming
 */
export function useLayoutPrimaryColor(primaryColor: string) {
  const setPrimaryColor = useContext(LayoutPrimaryColorContext);
  const currentColorRef = useRef<string | null>(null);

  useEffect(() => {
    // Only update if the color has actually changed
    if (currentColorRef.current !== primaryColor) {
      setPrimaryColor(primaryColor);
      currentColorRef.current = primaryColor;
    }

    // Reset to default on unmount - this should be handled by the component that sets initial values
    // We don't reset here because other components might want to maintain the color
  }, [primaryColor, setPrimaryColor]);
}

export interface AppLayoutProps extends PropsWithChildren {
  navigation: NavigationGroup[];
  userInfo?: UserInfo;
  businessInfo?: BusinessInfo;
  businessAccountId?: string;
  logoSrc: string;
  logoCollapsedSrc?: string;
  primaryColor?: string;
  logoutHandler: () => void;
  transitionClass?: string;
  backgroundClassName?: string;
  isAuthenticated?: boolean;
}

export default function AppLayout({
  children,
  navigation,
  userInfo,
  businessInfo,
  businessAccountId,
  logoSrc,
  logoCollapsedSrc,
  primaryColor,
  logoutHandler,
  transitionClass = '[&>*]:motion-safe:animate-in [&>*]:motion-safe:fade-in-25 [&>*]:motion-safe:slide-in-from-right-4 [&>*]:motion-safe:duration-\\[2000ms\\] [&>*]:motion-safe:ease-\\[cubic-bezier(0.4\\,0\\,0.2\\,1)\\] [&>*]:motion-safe:animate-out [&>*]:motion-safe:fade-out-25 [&>*]:motion-safe:slide-out-to-right-4',
  backgroundClassName,
  isAuthenticated = true,
}: AppLayoutProps) {
  // Background state for dynamic styling
  const defaultBg = backgroundClassName ?? 'bg-white';
  const [bgClass, setBgClass] = useState<string>(defaultBg);

  // Logo state for dynamic styling
  const [currentLogoSrc, setCurrentLogoSrc] = useState<string>(logoSrc);
  const [currentLogoCollapsedSrc, setCurrentLogoCollapsedSrc] = useState<
    string | undefined
  >(logoCollapsedSrc);

  // Primary color state for dynamic styling
  const [currentPrimaryColor, setCurrentPrimaryColor] = useState<string>(
    primaryColor || '#a21caf'
  );

  // Memoize the setBgClass function to prevent infinite loops in useLayoutBackground
  const memoizedSetBgClass = useCallback((className: string) => {
    setBgClass(className);
  }, []);

  // Memoize the setLogo function to prevent infinite loops in useLayoutLogo
  const memoizedSetLogo = useCallback(
    (logoSrc: string, logoCollapsedSrc?: string) => {
      setCurrentLogoSrc(logoSrc);
      setCurrentLogoCollapsedSrc(logoCollapsedSrc);
    },
    []
  );

  // Memoize the setPrimaryColor function to prevent infinite loops in useLayoutPrimaryColor
  const memoizedSetPrimaryColor = useCallback((primaryColor: string) => {
    setCurrentPrimaryColor(primaryColor);
  }, []);

  // Only set initial background from prop, don't override dynamic changes
  useEffect(() => {
    // Only set if backgroundClassName is explicitly provided
    if (backgroundClassName) {
      setBgClass(backgroundClassName);
    }
  }, [backgroundClassName]);

  // Update logo state when props change
  useEffect(() => {
    setCurrentLogoSrc(logoSrc);
    setCurrentLogoCollapsedSrc(logoCollapsedSrc);
  }, [logoSrc, logoCollapsedSrc]);

  // Update primary color state when props change
  useEffect(() => {
    setCurrentPrimaryColor(primaryColor || '#a21caf');
  }, [primaryColor]);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Initialize isCollapsed from localStorage, defaulting to false if not set
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  // Update localStorage when isCollapsed changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  return (
    <div className="flex h-full">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          {/* Backdrop transition */}
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-500 delay-100"
            enterFrom="opacity-0 backdrop-blur-none"
            enterTo="opacity-100 backdrop-blur-[8px]"
            leave="transition-all ease-in-out duration-500"
            leaveFrom="opacity-100 backdrop-blur-[8px]"
            leaveTo="opacity-0 backdrop-blur-none"
          >
            <div
              className="fixed inset-0 bg-slate-900/30 backdrop-blur-[8px] backdrop-contrast-100 will-change-[opacity,backdrop-filter]"
              aria-hidden="true"
            />
          </Transition.Child>

          <div className="fixed inset-0 flex overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="transform transition-all duration-700 ease-out delay-150"
              enterFrom="-translate-x-full opacity-40"
              enterTo="translate-x-0 opacity-100"
              leave="transform transition-all duration-500 ease-in-out"
              leaveFrom="translate-x-0 opacity-100"
              leaveTo="-translate-x-full opacity-40"
            >
              <Dialog.Panel
                className="relative mr-16 flex w-full max-w-xs flex-1 transform-gpu will-change-transform"
                style={{
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <div className="flex h-full w-full flex-1 flex-col bg-slate-50 shadow-xl">
                  {/* Close button */}
                  <Transition.Child
                    as={Fragment}
                    enter="transition-all ease-out duration-300 delay-300"
                    enterFrom="opacity-0 translate-x-2"
                    enterTo="opacity-100 translate-x-0"
                    leave="transition-all ease-in-out duration-300"
                    leaveFrom="opacity-100 translate-x-0"
                    leaveTo="opacity-0 translate-x-2"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="cursor-pointer shrink-0 inline-flex items-center gap-x-1.5 rounded-xl bg-white px-2 py-2 shadow-slate-900/20 text-sm font-bold text-slate-800 shadow-sm ring-1 ring-inset ring-slate-300/60 hover:bg-slate-50  transform-gpu transition-all duration-300 ease-out hover:scale-105 active:scale-95 hover:shadow-md"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-5 w-5 text-slate-800 transition-transform duration-200"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>

                  {/* Navigation content with staggered animations */}
                  <div className="w-full h-full">
                    <Navigation
                      isMobile={true}
                      navigation={navigation}
                      userInfo={userInfo}
                      businessInfo={businessInfo}
                      businessAccountId={businessAccountId}
                      logoSrc={currentLogoSrc}
                      logoCollapsedSrc={currentLogoCollapsedSrc}
                      primaryColor={currentPrimaryColor}
                      logoutHandler={logoutHandler}
                      closeSidebar={() => setSidebarOpen(false)}
                      isAuthenticated={isAuthenticated}
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar with collapse functionality */}
      <Navigation
        isMobile={false}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        navigation={navigation}
        userInfo={userInfo}
        businessInfo={businessInfo}
        businessAccountId={businessAccountId}
        logoSrc={currentLogoSrc}
        logoCollapsedSrc={currentLogoCollapsedSrc}
        primaryColor={currentPrimaryColor}
        logoutHandler={logoutHandler}
        isAuthenticated={isAuthenticated}
      />

      {/* Main content - adjust margin based on sidebar state */}
      <div
        className={cn(
          'flex-1 flex flex-col',
          'transition-all duration-300 ease-in-out',
          isCollapsed ? 'lg:pl-20' : 'lg:pl-72'
        )}
      >
        {/* Mobile header bar */}
        <div className="fixed w-full top-0 z-50 flex h-16 shrink-0 items-center gap-x-4 border-b border-slate-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-3 lg:px-8 lg:hidden">
          <button
            type="button"
            className="shrink-0 inline-flex items-center gap-x-1.5 rounded-lg bg-white px-2 py-2.5 text-sm font-bold text-slate-800 shadow-sm ring-1 ring-inset ring-slate-300/60 hover:bg-slate-50  h-[2.5rem]"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <div className="flex flex-col gap-[4px]">
              <div className="h-[2.5px] w-6 bg-current rounded-full"></div>
              <div className="h-[2.5px] w-[17.5px] bg-current rounded-full"></div>
              <div className="h-[2.5px] w-[12.5px] bg-current rounded-full"></div>
            </div>
          </button>

          {/* Separator */}
          <div className="h-6 w-px bg-slate-300 hidden" aria-hidden="true" />

          <div className="flex flex-1 gap-x-4 items-center justify-end">
            <div className="flex items-center gap-x-4">
              <button
                type="button"
                className="items-center justify-center text-slate-600 hover:text-slate-600 hidden"
              >
                <span className="sr-only">View notifications</span>
                <BellIcon className="h-6 w-6" aria-hidden="true" />
              </button>

              {/* Mobile/Tablet Profile Section */}
              <div className="lg:hidden flex items-center">
                {userInfo && (
                  <ProfileDropDown
                    isCollapsed={isCollapsed}
                    isMobileView={true}
                    hideNameAndBadge={true}
                    userInfo={userInfo}
                    logoutHandler={logoutHandler}
                    primaryColor={currentPrimaryColor}
                    isAuthenticated={isAuthenticated}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:p-4 lg:pl-0 sm:px-0 rounded-xl h-full overflow-y-auto">
          <main
            className={cn(
              'flex-1 relative h-full rounded-xl overflow-y-auto pt-16 lg:pt-0 lg:rounded-xl lg:rounded-tl-2xl lg:border lg:border-slate-200/40 shadow-[0px_0px_20px_rgba(0,0,0,0.1),_0px_0px_20px_rgba(255,255,255,0.90)] ring-[0.5px] ring-white/60',
              bgClass
            )}
          >
            <LayoutBackgroundContext.Provider value={memoizedSetBgClass}>
              <LayoutLogoContext.Provider value={memoizedSetLogo}>
                <LayoutPrimaryColorContext.Provider
                  value={memoizedSetPrimaryColor}
                >
                  <div className={cn('h-full', transitionClass)}>
                    {children}
                  </div>
                </LayoutPrimaryColorContext.Provider>
              </LayoutLogoContext.Provider>
            </LayoutBackgroundContext.Provider>
          </main>
        </div>
      </div>
    </div>
  );
}
